<template>
  <v-container>
    <div v-if="isFetchingData">
      <v-progress-linear :indeterminate="true" />Loading route info...
    </div>
    <v-layout v-if="!isFetchingData" id="calendarDayView" wrap>
      <v-layout id="calendarDayViewHeader">
        <v-flex xs6 class="text-xs-left pl-3 py-2">
          Address
        </v-flex>
        <v-flex xs6 class="text-xs-right py-2">
          Time
        </v-flex>
      </v-layout>
      <v-layout wrap>
        <v-flex
          v-for="(property, index) in propertiesList"
          :key="property._id"
          xs12
        >
          <v-layout wrap align-content-start class="row-calendar mt-3">
            <v-layout
              xs-12
              class="calendar-route-title mb-3"
              :class="{ 'custom-stops': !property.Status }"
            >
              <v-flex xs1 shrink class="number-calendar">
                {{ index + 1 }}.
              </v-flex>
              <v-flex
                xs8
                class="text-xs-left pl-1 overflow-hidden"
                @click="openProperty(property)"
              >
                {{ property.displayAddress }}
              </v-flex>
              <v-flex xs3 class="text-xs-right" @click="openProperty(property)">
                {{ property.arrivalTime | getTime }}
              </v-flex>
            </v-layout>
            <v-flex
              v-if="property.agent_message"
              xs12
              class="mobile-route-property-message"
            >
              <v-icon class="route-details-notification">
                error
              </v-icon>
              <span>{{ property.agent_message }}</span>
            </v-flex>
            <v-flex
              v-show="property.feedback && property.feedback !== null"
              xs6
              class="data-feedback text-xs-left pl-3 mb-3"
              :class="{
                'feedback-excellent': property.feedback >= 75,
                'feedback-bad': property.feedback < 40
              }"
            >
              {{ calculateFeedbackPercent(property) }}
            </v-flex>
            <v-flex v-if="property.propertyId" xs6 class="text-xs-right mb-3">
              <v-btn
                v-show="!property.feedback.match"
                class="button-toolbar"
                outline
                :color="colors.hwayzSun"
                round
                @click="onFeedbackModalOpen(property)"
              >
                PROVIDE FEEDBACK
              </v-btn>
              <v-btn
                v-show="property.feedback.match"
                :color="colors.hwayzTeal"
                class="button-toolbar"
                outline
                round
                @click="onFeedbackModalOpen(property)"
              >
                REVISE FEEDBACK
              </v-btn>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
      <v-layout wrap>
        <v-flex xs12>
          <google-location-map
            :key="route._id"
            :map-height="'300px'"
            :mobile-route="true"
            :route="routeInfo"
          />
        </v-flex>
        <v-flex xs12 class="text-xs-center">
          <v-btn
            class="button-show-direction mt-4"
            :color="$btnHwayzBlue"
            dark
            @click.stop="showDirections()"
          >
            Open Directions
          </v-btn>
        </v-flex>
      </v-layout>
    </v-layout>
  </v-container>
</template>

<script>
import moment from 'moment'
import { last } from 'lodash'
import { mapState, mapMutations, mapActions } from 'vuex'
import refreshClientData from '../mixins/refreshClientData'
import routeDataContent from '../mixins/routeDataContent'
import GoogleLocationMap from '../googlemap/GoogleLocationMap.vue'

import { calculateFeedbackMatch } from '../../utils/propertyUtils'
import colors from '../../scss/_colors.scss'
import {
  isCustomStartInRoute,
  isCustomEndInRoute,
  displayRouteDirections
} from '../../utils/routeUtils'
import { routeDirectionCalculateCheck } from '../../utils/planRouteUtils'
import { isEmpty } from '../../utils/generalUtils'

export default {
  filters: {
    getTime(val) {
      return val ? moment(val).format('hh:mm A') : 'Loading...'
    }
  },

  components: { GoogleLocationMap },

  mixins: [refreshClientData, routeDataContent],

  props: {
    route: {
      type: Object,
      required: true
    }
  },

  data: () => ({
    routeInfo: {},
    propertiesList: [],
    isFetchingData: true,
    colors: colors
  }),

  computed: {
    ...mapState('common', ['profileStatus'])
  },

  created() {
    this.fetchProperties()
  },

  destroyed() {
    this.getRouteData({ data: this.agentData, type: 'agent' })
  },

  methods: {
    ...mapActions('agent', ['setSearchedClient']),
    ...mapActions('common', ['getRouteData']),
    ...mapMutations('common', {
      setPersonName: 'SET_PERSON_NAME',
      setSelectedHome: 'SET_SELECTED_HOME'
    }),
    ...mapMutations('client', {
      setClientData: 'SET_CLIENT_DATA'
    }),

    showDirections() {
      displayRouteDirections(this.routeInfo.calculatedArray)
    },

    onFeedbackModalOpen(data) {
      if (
        this.routeInfo.routeStops[0].clientData &&
        this.routeInfo.routeStops[0].clientData.homePrefs
      ) {
        this.setSelectedHome({ ...data, _id: data.favoriteId })
        if (this.profileStatus === 'client') {
          this.setPersonName(
            this.routeInfo.routeStops[0].clientData
              ? this.routeInfo.routeStops[0].clientData.name
              : ''
          )
        } else {
          this.setPersonName(this.agentData.name)
        }
        this.setClientData(this.routeInfo.routeStops[0].clientData)
        this.$emit('select-home', data)
      }
    },

    openProperty(property) {
      if (property.propertyId) this.$emit('show-property', property)
    },

    getPropertyInfo(updatedRoute) {
      let properties = []
      let counter = 1

      if (!isEmpty(updatedRoute.fullCalculatedArray)) {
        properties = updatedRoute.fullCalculatedArray
          .map(property => {
            return {
              ...property,
              displayAddress: `${property.Address} ${property.City} ${property.PostalCode}`
            }
          })
          .filter(item => item.arrivalTime !== null)
        properties.forEach(item => {
          const history = last(item.history)
          if (history && history.message) {
            item.agent_message = history.message
          }
        })
        this.setSearchedClient(this.route.routeStops[0].clientData)
      } else {
        const clientAssociatedWithRoute = this.agentData.clients.find(
          client => client._id === this.route.clientId
        )
        this.setSearchedClient(clientAssociatedWithRoute)
      }

      if (isCustomStartInRoute(this.route)) {
        const firstElement = updatedRoute.calculatedArray[0]
        properties.unshift({
          _id: counter,
          displayAddress: firstElement.Address,
          arrivalTime: firstElement.arrivalTime,
          Latitude: firstElement.latitude,
          Longitude: firstElement.longitude,
          propertyId: null
        })
        counter++
      }

      if (isCustomEndInRoute(this.route)) {
        const lastElement =
          updatedRoute.calculatedArray[updatedRoute.calculatedArray.length - 1]
        properties.push({
          _id: counter,
          displayAddress: lastElement.Address,
          arrivalTime: lastElement.arrivalTime,
          Latitude: lastElement.latitude,
          Longitude: lastElement.longitude,
          propertyId: null
        })
      }

      return properties
    },

    calculateFeedbackPercent(home) {
      if (home.feedback && home.feedback.match) {
        return `${parseInt(calculateFeedbackMatch(home.feedback.match))}%`
      }
      return '0%'
    },

    async fetchProperties() {
      this.isFetchingData = true
      const updatedRoute = routeDirectionCalculateCheck(this.route)
        ? await this.calculateRouteHomeMarkerArray(this.route)
        : { ...this.route }
      this.propertiesList = this.getPropertyInfo(updatedRoute)
      this.routeInfo = { ...updatedRoute }
      this.isFetchingData = false
    }
  }
}
</script>

<style lang="sass" scoped>
.calendar-route-title
  background-color: rgba($hwayzBrandBlue, 0.9)
  color: $hwayzWhite
  padding: 6px 12px 6px 1px
  border-radius: 12px
  &.custom-stops
    background-color: rgba($hwayzDarkGray, 0.9)

// MY STYLE
#calendarDayView
  color: $hwayzBlack
  background-color: $hwayzWhite

#calendarDayViewHeader
  // font-weight: bold
  font-size: 1.8rem
  border-bottom: 2px solid $hwayzBlack

.row-calendar
  font-size: 1.8rem
  border-bottom: 2px solid $hwayzLightGray

.button-toolbar
  cursor: pointer
  font-size: 1.5rem !important
  font-weight: bold
  width: 16rem
  margin: 0
  margin-top: 1rem
  i
    color: $hwayzWhite !important
  @media only screen and (max-width: 360px)
    width: 14rem
    height: 4rem
    font-size: 1.3rem !important

.mobile-route-property-message
  text-align: left
  padding-left: 2rem
  overflow: hidden
  font-size: 1.4rem
  display: flex
  align-items: center
  margin-bottom: 2rem
  border-radius: 15px
  padding: 5px 10px
  z-index: 1
  background: $hwayzLightGray

.data-feedback
  font-size: 3.5rem

.feedback-excellent
  color: $hwayzGreen

.feedback-bad
  color: $hwayzRed

.hwayzSun
  background-color: $hwayzSun !important

.hwayzTeal
  background-color: $hwayzTeal !important

// WAS HERE
.v-calendar,
.calendar__feature-pane
  box-shadow: 0 0 1rem rgba($black, 0.3) !important
  padding: 2rem !important
  border-radius: 1rem
  height: 50px !important
  display: table
  width: 100%
  table-layout: fixed
  border-spacing: 10px
.calendar_select
  width: 50%
  display: table-cell
.v-calendar
  min-height: 100px !important
  max-height: 800px !important

.calendar__feature-pane
  position: relative
  margin-bottom: 2rem

.calendar__btn-navigation
  display: table-cell
  width: 20%
  button
    color: $hwayzBlue !important

@media only screen and (min-width: 600px)
  .v-calendar
    min-height: 500px !important

@media only screen and (min-width: 960px)
  .v-calendar,
  .calendar__feature-pane
    min-height: 50px !important

  .calendar__feature-pane
    margin-bottom: 0rem
</style>
